/*
 * Copyright (c) 2024 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
import { atom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'

import { geolocationAtom } from '@/store/geolocation'
import { Country, getCountryByCode, getCountryByLocale } from '@/utils/countries'
import { navigatorLocale } from '@/utils/navigator-locale'

/**
 * An atom, using local storage, to store the selected country. Initially uses navigator locale, but may be updated by
 * geolocation on first visit.
 */
const selectedCountryAtom = atomWithStorage<Country>(
  'selectedCountry',
  getCountryByLocale(navigatorLocale),
  undefined,
  {
    getOnInit: true,
  },
)
selectedCountryAtom.debugLabel = 'selectedCountryAtom'

/**
 * Effect atom that initialises the country on first load if needed. It uses the geolocation data if available,
 * otherwise it uses the navigator locale.
 */
const initialiseCountryAtom = atom(null, (get, set) => {
  const hasInitialised = localStorage.getItem('countryInitialised')

  if (!hasInitialised) {
    const geolocation = get(geolocationAtom)
    if (geolocation?.country) {
      set(selectedCountryAtom, getCountryByCode(geolocation.country))
    } else {
      set(selectedCountryAtom, getCountryByLocale(navigatorLocale))
    }

    localStorage.setItem('countryInitialised', 'true')
  }
})

export { initialiseCountryAtom, selectedCountryAtom }
